import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  addEdge,
  MarkerType,
  Position,
  useNodesState,
  useEdgesState
} from '@xyflow/react';
import dagre from '@dagrejs/dagre';
import '@xyflow/react/dist/style.css';
import EditableNode from './InlineNodes/EditableNode';
import { NODE_LIBRARY, defaultConfig, makeNode, uid } from './InlineNodes/nodeConfig';
import { useAPI } from '../../Context/MyContext';
import { toast } from 'react-hot-toast';

const nodeTypes = {
  editableNode: EditableNode
};

function mapLegacyType(type) {
  if (type === 'trigger') return 'trigger';
  if (type === 'message_send') return 'send_message';
  if (type === 'ai_response') return 'ai';
  if (type === 'knowledge_base') return 'knowledge_base';
  if (type === 'conditional') return 'conditional';
  if (type === 'api_call') return 'api_call';
  return 'api_call';
}

function mapKindToStepType(kind) {
  if (kind === 'send_message') return 'message_send';
  if (kind === 'ai') return 'ai_response';
  if (kind === 'knowledge_base') return 'knowledge_base';
  if (kind === 'conditional') return 'conditional';
  if (kind === 'api_call') return 'api_call';
  return 'api_call';
}

function parseJsonObject(value, fallback = {}) {
  if (value == null || value === '') return fallback;
  if (typeof value === 'object' && !Array.isArray(value)) return value;
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : fallback;
  } catch {
    return fallback;
  }
}

function stringifyJson(value, fallback = '{\n}') {
  try {
    return JSON.stringify(value ?? {}, null, 2);
  } catch {
    return fallback;
  }
}

function normalizeConfig(kind, config = {}) {
  const base = { ...defaultConfig(kind), ...config };

  if (kind === 'api_call') {
    const headers = parseJsonObject(base.headersJson, parseJsonObject(base.headers, {}));
    const query = parseJsonObject(base.queryJson, parseJsonObject(base.query, {}));
    const body = parseJsonObject(base.bodyJson, parseJsonObject(base.body, {}));

    return {
      ...base,
      headers,
      query,
      body,
      headersJson: stringifyJson(headers),
      queryJson: stringifyJson(query),
      bodyJson: stringifyJson(body),
      successVariable: base.successVariable || base.saveResponseAs || 'api.response'
    };
  }

  if (kind === 'send_message') {
    const payload = base.payload && typeof base.payload === 'object' ? base.payload : {};
    const messageType = base.messageType || 'text';
    const textBody = payload?.text?.body || base.message || base.text || '';
    const mediaPayload = payload?.[messageType] || {};

    return {
      ...base,
      platform: base.platform || 'whatsapp',
      to: base.to || base.recipientSource || '{{recipient_id}}',
      phoneNumberId: base.phoneNumberId || '{{phone_number_id}}',
      recipientSource: base.recipientSource || base.to || 'contact.phone',
      message: textBody,
      text: textBody,
      templateId: base.templateId || payload?.template?.template_id || '',
      templateName: base.templateName || payload?.template?.name || '',
      templateLanguage: base.templateLanguage || payload?.template?.language || 'en_US',
      mediaUrl: base.mediaUrl || mediaPayload?.link || '',
      mediaCaption: base.mediaCaption || mediaPayload?.caption || '',
      locationName: base.locationName || payload?.location?.name || '',
      latitude: base.latitude || payload?.location?.latitude || '',
      longitude: base.longitude || payload?.location?.longitude || '',
      payload: payload.text ? payload : { ...payload, text: { body: textBody } }
    };
  }

  if (kind === 'ai') {
    return {
      ...base,
      provider: base.provider || 'whatgrowpro',
      apiKey: base.apiKey || '',
      systemPrompt: base.systemPrompt || base.prompt?.system || '',
      temperature: base.temperature ?? base.generation?.temperature ?? 0.2,
      maxTokens: base.maxTokens ?? base.generation?.maxTokens ?? 350,
      outputVariable: base.outputVariable || base.responseField || 'ai.output_text',
      prompt: {
        ...(base.prompt || {}),
        system: base.prompt?.system || base.systemPrompt || ''
      },
      generation: {
        ...(base.generation || {}),
        temperature: base.generation?.temperature ?? base.temperature ?? 0.2,
        maxTokens: base.generation?.maxTokens ?? base.maxTokens ?? 350
      },
      responseField: base.responseField || base.outputVariable || 'ai.output_text'
    };
  }

  if (kind === 'knowledge_base') {
    return {
      ...base,
      provider: base.provider || 'whatgrowpro',
      apiKey: base.apiKey || '',
      model: base.model || 'whatgrowpro',
      prompt: {
        ...(base.prompt || {}),
        system: base.prompt?.system || ''
      },
      outputVariable: base.outputVariable || base.responseField || 'kb.context',
      responseField: base.responseField || base.outputVariable || 'aiReply'
    };
  }

  if (kind === 'conditional') {
    const firstRule = Array.isArray(base.rules) && base.rules.length > 0 ? base.rules[0] : null;
    return {
      ...base,
      source: base.source || firstRule?.left || 'message.text.body',
      operator: base.operator || firstRule?.operator || 'contains',
      value: base.value || firstRule?.right || '',
      trueNode: base.trueNode || '',
      falseNode: base.falseNode || ''
    };
  }

  return base;
}

function serializeConfigForStorage(kind, config = {}) {
  if (kind === 'api_call') {
    const headers = parseJsonObject(config.headersJson, parseJsonObject(config.headers, {}));
    const query = parseJsonObject(config.queryJson, parseJsonObject(config.query, {}));
    const body = parseJsonObject(config.bodyJson, parseJsonObject(config.body, {}));
    const successVariable = config.successVariable || config.saveResponseAs || 'api.response';

    return {
      url: config.url || '',
      method: config.method || 'POST',
      headers,
      query,
      body,
      saveResponseAs: config.saveResponseAs || successVariable,
      authType: config.authType || 'none',
      token: config.token || '',
      timeoutMs: Number(config.timeoutMs) || 10000,
      retryCount: Number(config.retryCount) || 1,
      successVariable,
      errorVariable: config.errorVariable || 'api.error',
      failStrategy: config.failStrategy || 'continue'
    };
  }

  if (kind === 'send_message') {
    const messageType = config.messageType || 'text';
    const payload = config.payload && typeof config.payload === 'object' ? { ...config.payload } : {};

    if (messageType === 'text') {
      payload.text = { body: config.text || config.message || payload?.text?.body || '' };
    } else if (messageType === 'template') {
      payload.template = {
        ...(payload.template || {}),
        template_id: config.templateId || payload?.template?.template_id || '',
        name: config.templateName || payload?.template?.name || '',
        language: config.templateLanguage || payload?.template?.language || 'en_US',
        phone_number_id: payload?.template?.phone_number_id || config.phoneNumberId || '{{phone_number_id}}'
      };
    } else if (['image', 'video', 'document', 'audio', 'sticker'].includes(messageType)) {
      payload[messageType] = {
        ...(payload[messageType] || {}),
        link: config.mediaUrl || payload?.[messageType]?.link || '',
        caption: config.mediaCaption || payload?.[messageType]?.caption || ''
      };
    } else if (messageType === 'location') {
      payload.location = {
        ...(payload.location || {}),
        name: config.locationName || payload?.location?.name || '',
        latitude: config.latitude || payload?.location?.latitude || '',
        longitude: config.longitude || payload?.location?.longitude || ''
      };
    }

    return {
      platform: config.platform || 'whatsapp',
      to: config.to || config.recipientSource || '{{recipient_id}}',
      phoneNumberId: config.phoneNumberId || '{{phone_number_id}}',
      message: config.text || config.message || payload?.text?.body || '',
      messageType,
      payload
    };
  }

  if (kind === 'ai') {
    const systemPrompt = config.systemPrompt || config.prompt?.system || '';
    const temperature = config.temperature ?? config.generation?.temperature ?? 0.2;
    const maxTokens = config.maxTokens ?? config.generation?.maxTokens ?? 350;
    const outputVariable = config.outputVariable || config.responseField || 'ai.output_text';

    return {
      provider: config.provider || 'whatgrowpro',
      model: config.model || 'whatgrowpro',
      apiKey: config.apiKey || '',
      systemPrompt,
      userPrompt: config.userPrompt || '',
      temperature,
      maxTokens,
      inputContextJson: config.inputContextJson || '{\n}',
      outputVariable,
      prompt: {
        ...(config.prompt || {}),
        system: config.prompt?.system || systemPrompt
      },
      generation: {
        ...(config.generation || {}),
        temperature: config.generation?.temperature ?? temperature,
        maxTokens: config.generation?.maxTokens ?? maxTokens
      },
      responseField: config.responseField || outputVariable
    };
  }

  if (kind === 'knowledge_base') {
    const outputVariable = config.outputVariable || config.responseField || 'kb.context';
    return {
      provider: config.provider || 'whatgrowpro',
      model: config.model || 'whatgrowpro',
      apiKey: config.apiKey || '',
      prompt: {
        ...(config.prompt || {}),
        system: config.prompt?.system || ''
      },
      kbId: config.kbId || '',
      queryTemplate: config.queryTemplate || '',
      topK: Number(config.topK) || 5,
      minScore: Number(config.minScore) || 0.65,
      includeCitations: Boolean(config.includeCitations),
      outputVariable,
      responseField: config.responseField || outputVariable,
      fallbackBehavior: config.fallbackBehavior || 'continue_without_context'
    };
  }

  if (kind === 'conditional') {
    const rules = Array.isArray(config.rules) ? config.rules : [];
    const legacyRule = config.source || config.operator || config.value
      ? [{
        id: uid('rule'),
        left: config.source || 'message.text.body',
        operator: config.operator || 'contains',
        right: config.value || ''
      }]
      : [];

    return {
      logic: config.logic || 'all',
      rules: rules.length ? rules : legacyRule,
      source: config.source || '',
      operator: config.operator || '',
      value: config.value || '',
      trueNode: config.trueNode || '',
      falseNode: config.falseNode || ''
    };
  }

  return config;
}

function getStepId(step, fallbackPrefix = 'node') {
  const fromOid = step?._id && typeof step._id === 'object' ? step._id.$oid : null;
  const value = step?.id || step?._id || fromOid;
  return String(value || uid(fallbackPrefix));
}

function mergeConfig(kind, config = {}) {
  return normalizeConfig(kind, config);
}

function normalizeConditionOutcome(value) {
  if (value === true || value === 'true') return 'true';
  if (value === false || value === 'false') return 'false';
  return undefined;
}

function deriveConditionOutcomeFromEdge(edge = {}) {
  const explicit = normalizeConditionOutcome(edge.conditionOutcome);
  if (explicit) return explicit;
  if (edge.sourceHandle === 'true' || edge.sourceHandle === 'false') return edge.sourceHandle;
  const label = String(edge.label || '').trim().toLowerCase();
  if (label === 'true' || label === 'false') return label;
  return undefined;
}

// function hydrateFromWorkflow(workflow) {
//   if (!workflow) return { nodes: [], edges: [] };
//   const ordered = [...(workflow.steps || [])].sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
//   console.log("workflow is are:", workflow)
//   if (workflow.edges?.length) {
//     const stepsWithId = ordered.map((step, index) => {
//       const id = getStepId(step, 'step');
//       return { step, id, index };
//     });


//     const stepIdSet = new Set(stepsWithId.map((item) => item.id));
//     const triggerEdge = workflow.edges.find((edge) => !stepIdSet.has(String(edge.source)));
//     const triggerId = triggerEdge?.source ? String(triggerEdge.source) : 'trigger';

//     const triggerNode = makeNode('trigger', { x: 120, y: 80 });
//     triggerNode.id = triggerId;
//     triggerNode.data.config = mergeConfig('trigger', {
//       triggerType: workflow.triggerType || 'webhook_event'
//     });

//     const stepNodes = stepsWithId.map(({ step, id, index }) => {
//       const kind = mapLegacyType(step.type);
//       const node = makeNode(kind, step.position || { x: 560 + index * 460, y: 80 });
//       node.id = id;
//       node.data.config = mergeConfig(kind, step.config || {});
//       return node;
//     });

//     // const allNodes = [triggerNode, ...stepNodes];
//     // const validIds = new Set(allNodes.map((node) => node.id));
//     // const graphEdges = workflow.edges
//     //   .filter((edge) => validIds.has(String(edge.source)) && validIds.has(String(edge.target)))
//     //   .map((edge) => ({
//     //     id: String(edge.id || uid('edge')),
//     //     source: String(edge.source),
//     //     target: String(edge.target),
//     //     type: edge.type || 'default',
//     //     label: edge.label || '',
//     //     sourceHandle: edge.sourceHandle || undefined,
//     //     markerEnd: { type: MarkerType.ArrowClosed }
//     //   }));

//     // return { nodes: allNodes, edges: graphEdges };
//     const allNodes = [triggerNode, ...stepNodes];

//     const validIds = new Set(allNodes.map(node => String(node.id)));
// console.log("Nodes:", allNodes);
// console.log("Edges:", workflow.edges);

//     const graphEdges = (workflow.edges || [])
//       .filter(edge =>
//         validIds.has(String(edge.source)) &&
//         validIds.has(String(edge.target))
//       )
//       .map(edge => ({
//         id: String(edge.edgeId || uid("edge")),
//         source: String(edge.source),
//         target: String(edge.target),
//         type: edge.type || "default",
//         label: edge.label || "",
//         sourceHandle: edge.sourceHandle || undefined,
//         markerEnd: { type: MarkerType.ArrowClosed }
//       }));

//     return { nodes: allNodes, edges: graphEdges };
//   }

//   const nodes = [];
//   const edges = [];
//   const rowRef = { value: 0 };
//   const triggerNode = makeNode('trigger', { x: 120, y: 80 });
//   triggerNode.id = 'trigger';
//   triggerNode.data.config = mergeConfig('trigger', { triggerType: workflow.triggerType || 'webhook_event' });
//   nodes.push(triggerNode);
//   let rootId = triggerNode.id;

//   function placeNode(step, parentId = null, depth = 0, branch = null) {
//     const row = rowRef.value++;
//     const kind = mapLegacyType(step.type);
//     const node = makeNode(kind, {
//       x: step.position?.x ?? (120 + depth * 460),
//       y: step.position?.y ?? (80 + row * 280)
//     });
//     node.id = getStepId(step, 'step');

//     node.data.config = mergeConfig(kind, step.config || {});
//     nodes.push(node);

//     if (parentId) {
//       edges.push({
//         id: uid('edge'),
//         source: parentId,
//         target: node.id,
//         markerEnd: { type: MarkerType.ArrowClosed },
//         ...(branch ? { sourceHandle: branch, label: branch === 'true' ? 'True' : 'False' } : {})
//       });
//     }

//     if (kind === 'conditional') {
//       (step.config?.stepsIfTrue || []).forEach((child) => placeNode(child, node.id, depth + 1, 'true'));
//       (step.config?.stepsIfFalse || []).forEach((child) => placeNode(child, node.id, depth + 1, 'false'));
//     }

//     return node.id;
//   }

//   let previous = rootId;
//   ordered.forEach((step) => {
//     previous = placeNode(step, previous, 0, null);
//   });

//   return { nodes, edges };
// }


function hydrateFromWorkflow(workflow) {
  if (!workflow) return { nodes: [], edges: [] };

  const triggerNode = makeNode("trigger", { x: 120, y: 80 });
  triggerNode.id = "trigger";
  triggerNode.data.config = mergeConfig("trigger", {
    triggerType: workflow.triggerType || "webhook_event"
  });

  // const stepNodes = (workflow.steps || []).map((step) => {
  //   const kind = mapLegacyType(step.type);

  //   const node = makeNode(
  //     kind,
  //     step.position || { x: 560, y: 80 }
  //   );

  //   node.id = String(step.stepId);
  //   node.data.config = mergeConfig(kind, step.config || {});

  //   return node;
  // });

  const stepNodes = (workflow.steps || []).map((step, index) => {
  const kind = mapLegacyType(step.type);
  const pos = step.position || { x: 560 + index * 460, y: 80 + index * 280 }; // stagger positions
  const node = makeNode(kind, pos);
  // node.id = String(step.stepId);
  node.id = String(step.stepId || step.id || uid('step'));
  node.data.config = mergeConfig(kind, step.config || {});
  return node;
});

  const nodes = [triggerNode, ...stepNodes];

  const edges = (workflow.edges || []).map((edge) => {
    const conditionOutcome = deriveConditionOutcomeFromEdge(edge);
    const sourceHandle = edge.sourceHandle || conditionOutcome;
    const label = edge.label || (conditionOutcome === 'true' ? 'True' : conditionOutcome === 'false' ? 'False' : '');

    return {
    id: String(edge.edgeId || edge.id || uid('edge')),
    source: String(edge.source),
    target: String(edge.target),
    type: edge.type || "default",
    label,
    sourceHandle: sourceHandle || undefined,
    conditionOutcome,
    markerEnd: { type: MarkerType.ArrowClosed }
  };
  });

  // console.log("Hydrated nodes:", nodes);
  // console.log("Hydrated edges:", edges);

  return { nodes, edges };
}

function getDagreLayout(nodes, edges, direction = 'LR') {
  const dagreGraph = new dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}));
  dagreGraph.setGraph({ rankdir: direction, ranksep: 100, nodesep: 70 });

  nodes.forEach((node) => {
    const width = node.measured?.width || node.width || 420;
    const height = node.measured?.height || node.height || 220;
    dagreGraph.setNode(node.id, { width, height });
  });

  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  dagre.layout(dagreGraph);

  const isHorizontal = direction === 'LR';
  const targetPosition = isHorizontal ? Position.Left : Position.Top;
  const sourcePosition = isHorizontal ? Position.Right : Position.Bottom;

  return nodes.map((node) => {
    const dagreNode = dagreGraph.node(node.id);
    const width = node.measured?.width || node.width || 420;
    const height = node.measured?.height || node.height || 220;

    return {
      ...node,
      targetPosition,
      sourcePosition,
      position: {
        x: dagreNode.x - width / 2,
        y: dagreNode.y - height / 2
      }
    };
  });
}

function getStructuredPositions(nodes, edges) {
  const NODE_GAP_X = 460;
  const NODE_GAP_Y = 280;
  const START_X = 120;
  const START_Y = 80;

  const ids = nodes.map((n) => n.id);
  const idSet = new Set(ids);
  const indegree = new Map(ids.map((id) => [id, 0]));
  const outgoing = new Map(ids.map((id) => [id, []]));

  edges.forEach((edge) => {
    if (!idSet.has(edge.source) || !idSet.has(edge.target) || edge.source === edge.target) return;
    outgoing.get(edge.source).push(edge.target);
    indegree.set(edge.target, (indegree.get(edge.target) || 0) + 1);
  });

  const queue = ids.filter((id) => (indegree.get(id) || 0) === 0);
  const level = new Map(queue.map((id) => [id, 0]));
  const visited = new Set();

  while (queue.length) {
    const current = queue.shift();
    visited.add(current);
    const currentLevel = level.get(current) || 0;

    (outgoing.get(current) || []).forEach((nextId) => {
      level.set(nextId, Math.max(level.get(nextId) || 0, currentLevel + 1));
      indegree.set(nextId, (indegree.get(nextId) || 0) - 1);
      if ((indegree.get(nextId) || 0) === 0) queue.push(nextId);
    });
  }

  ids.forEach((id) => {
    if (!visited.has(id) && !level.has(id)) level.set(id, 0);
  });

  const levels = new Map();
  nodes.forEach((node) => {
    const l = level.get(node.id) || 0;
    if (!levels.has(l)) levels.set(l, []);
    levels.get(l).push(node.id);
  });

  const positions = {};
  Array.from(levels.keys())
    .sort((a, b) => a - b)
    .forEach((l) => {
      const levelNodeIds = levels.get(l);
      levelNodeIds.forEach((nodeId, index) => {
        positions[nodeId] = { x: START_X + l * NODE_GAP_X, y: START_Y + index * NODE_GAP_Y };
      });
    });

  return positions;
}

export default function WorkflowBuilder({ workflow, focusMode = false }) {
  const { updateWorkflow } = useAPI();

  const [workflowMeta, setWorkflowMeta] = useState({
    name: 'Automation Workflow',
    description: '',
    trigger: 'incoming_message',
    timezone: 'America/New_York',
    status: 'DRAFT'
  });

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [notice, setNotice] = useState('Drag node from left panel and drop on canvas.');
  const [rf, setRf] = useState(null);
  const [selectedEdgeId, setSelectedEdgeId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!workflow) {
      const trigger = makeNode('trigger', { x: 120, y: 80 });
      trigger.id = 'trigger';
      const kb = makeNode('knowledge_base', { x: 560, y: 80 });
      const ai = makeNode('ai', { x: 1000, y: 80 });
      const conditional = makeNode('conditional', { x: 1440, y: 80 });
      setNodes([trigger, kb, ai, conditional]);
      setEdges([
        { id: uid('edge'), source: trigger.id, target: kb.id, markerEnd: { type: MarkerType.ArrowClosed } },
        { id: uid('edge'), source: kb.id, target: ai.id, markerEnd: { type: MarkerType.ArrowClosed } },
        { id: uid('edge'), source: ai.id, target: conditional.id, markerEnd: { type: MarkerType.ArrowClosed } }
      ]);
      setSelectedEdgeId(null);
      return;
    }

    const hydrated = hydrateFromWorkflow(workflow);
    setNodes(hydrated.nodes);
    setEdges(hydrated.edges);
    setSelectedEdgeId(null);
    setWorkflowMeta({
      name: workflow.name || 'Automation Workflow',
      description: workflow.description || '',
      trigger: workflow.triggerType || 'incoming_message',
      timezone: workflow.timezone || 'America/New_York',
      status: workflow.isActive ? 'PUBLISHED' : 'DRAFT'
    });
  }, [workflow, setNodes, setEdges]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (!selectedEdgeId) return;
      if (event.key === 'Delete' || event.key === 'Backspace') {
        event.preventDefault();
        setEdges((prev) => prev.filter((edge) => edge.id !== selectedEdgeId));
        setNotice('Selected edge deleted.');
        setSelectedEdgeId(null);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [selectedEdgeId, setEdges]);

  const onConnect = useCallback(
    (params) => {
      if (params.source === params.target) return;
      const conditionOutcome =
        params.sourceHandle === 'true' || params.sourceHandle === 'false'
          ? params.sourceHandle
          : undefined;
      setEdges((eds) =>
        addEdge(
          {
            ...params,
            markerEnd: { type: MarkerType.ArrowClosed },
            label: params.sourceHandle === 'true' ? 'True' : params.sourceHandle === 'false' ? 'False' : '',
            conditionOutcome
          },
          eds
        )
      );
    },
    [setEdges]
  );

  const patchNode = useCallback(
    (nodeId, patch) => {
      setNodes((prev) => prev.map((node) => (node.id === nodeId ? { ...node, data: { ...node.data, config: { ...node.data.config, ...patch } } } : node)));
    },
    [setNodes]
  );

  const removeNode = useCallback(
    (nodeId) => {
      setNodes((prev) => prev.filter((n) => n.id !== nodeId));
      setEdges((prev) => prev.filter((e) => e.source !== nodeId && e.target !== nodeId));
    },
    [setNodes, setEdges]
  );

  const duplicateNode = useCallback(
    (nodeId) => {
      const source = nodes.find((n) => n.id === nodeId);
      if (!source) return;
      const next = { ...source, id: uid('node'), position: { x: source.position.x + 40, y: source.position.y + 40 } };
      setNodes((prev) => [...prev, next]);
    },
    [nodes, setNodes]
  );

  const addRule = useCallback(
    (nodeId) => {
      setNodes((prev) =>
        prev.map((n) => {
          if (n.id !== nodeId || n.data.kind !== 'conditional') return n;
          const rules = n.data.config.rules || [];
          return {
            ...n,
            data: {
              ...n.data,
              config: {
                ...n.data.config,
                rules: [...rules, { id: uid('rule'), left: '', operator: 'equals', right: '' }]
              }
            }
          };
        })
      );
    },
    [setNodes]
  );

  const patchRule = useCallback(
    (nodeId, ruleId, patch) => {
      setNodes((prev) =>
        prev.map((n) => {
          if (n.id !== nodeId || n.data.kind !== 'conditional') return n;
          return {
            ...n,
            data: {
              ...n.data,
              config: {
                ...n.data.config,
                rules: (n.data.config.rules || []).map((rule) => (rule.id === ruleId ? { ...rule, ...patch } : rule))
              }
            }
          };
        })
      );
    },
    [setNodes]
  );

  const removeRule = useCallback(
    (nodeId, ruleId) => {
      setNodes((prev) =>
        prev.map((n) => {
          if (n.id !== nodeId || n.data.kind !== 'conditional') return n;
          return {
            ...n,
            data: {
              ...n.data,
              config: {
                ...n.data.config,
                rules: (n.data.config.rules || []).filter((rule) => rule.id !== ruleId)
              }
            }
          };
        })
      );
    },
    [setNodes]
  );

  const renderNodes = useMemo(
    () =>
      nodes.map((node) => ({
        ...node,
        data: {
          kind: node.data.kind, // ensure kind is preserved
          ...node.data,
          workflowPhoneId: workflow?.phone_number_id || '',
          onPatch: (patch) => patchNode(node.id, patch),
          onRemove: () => removeNode(node.id),
          onDuplicate: () => duplicateNode(node.id),
          onRuleAdd: () => addRule(node.id),
          onRulePatch: (ruleId, patch) => patchRule(node.id, ruleId, patch),
          onRuleRemove: (ruleId) => removeRule(node.id, ruleId)
        }
      })),
    [nodes, workflow?.phone_number_id, patchNode, removeNode, duplicateNode, addRule, patchRule, removeRule]
  );



  const onDragStart = (event, kind) => {
    event.dataTransfer.setData('application/reactflow', kind);
    event.dataTransfer.effectAllowed = 'move';
  };

  const onDragOver = (event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  };

  const onDrop = (event) => {
    event.preventDefault();
    const kind = event.dataTransfer.getData('application/reactflow');
    if (!kind || !rf) return;
    const position = rf.screenToFlowPosition({ x: event.clientX, y: event.clientY });
    setNodes((prev) => [...prev, makeNode(kind, position)]);
    setNotice(`${NODE_LIBRARY.find((n) => n.kind === kind)?.title || 'Node'} added.`);
  };

  const arrangeWorkflow = useCallback(() => {
    const positions = getStructuredPositions(nodes, edges);
    setNodes((prev) => prev.map((node) => ({ ...node, position: positions[node.id] || node.position })));
    setNotice('Workflow arranged based on edge structure.');
    requestAnimationFrame(() => rf?.fitView({ padding: 0.2, duration: 300 }));
  }, [nodes, edges, setNodes, rf]);

  const applyDagreLayout = useCallback(
    (direction) => {
      const nextNodes = getDagreLayout(nodes, edges, direction);
      setNodes(nextNodes);
      setNotice(`Workflow laid out with Dagre (${direction}).`);
      requestAnimationFrame(() => rf?.fitView({ padding: 0.2, duration: 300 }));
    },
    [nodes, edges, setNodes, rf]
  );

  const saveWorkflow = useCallback(async () => {
    if (!workflow?._id) {
      toast.error('Select an existing workflow to save.');
      return;
    }

    try {
      setIsSaving(true);

      const orderedNodes = [...nodes].sort((a, b) => {
        if ((a.position?.x || 0) !== (b.position?.x || 0)) return (a.position?.x || 0) - (b.position?.x || 0);
        return (a.position?.y || 0) - (b.position?.y || 0);
      });

      const steps = orderedNodes
        .filter((node) => node.data?.kind !== 'trigger')
        .map((node, index) => ({
          id: node.id,
          type: mapKindToStepType(node.data?.kind),
          sequence: index + 1,
          config: serializeConfigForStorage(node.data?.kind, node.data?.config || {}),
          position: {
            x: node.position?.x || 0,
            y: node.position?.y || 0
          }
        }));

      const stepMap = new Map(steps.map((step) => [String(step.id), step]));

      const graphEdges = edges.map((edge) => ({
        id: edge.id,
        source: edge.source,
        target: edge.target,
        type: edge.type || 'default',
        label: edge.label || '',
        sourceHandle: edge.sourceHandle || '',
        conditionOutcome: deriveConditionOutcomeFromEdge(edge) || null
      }));

      graphEdges.forEach((edge) => {
        const step = stepMap.get(String(edge.source));
        if (!step || step.type !== 'conditional') return;

        const conditionOutcome = deriveConditionOutcomeFromEdge(edge);
        if (!conditionOutcome) return;

        const key = conditionOutcome === 'true' ? 'trueNode' : 'falseNode';
        if (!step.config || typeof step.config !== 'object') {
          step.config = {};
        }
        step.config[key] = String(edge.target);
      });

      const payload = {
        workflowId: workflow._id,
        name: workflowMeta.name,
        description: workflowMeta.description || '',
        triggerType: workflowMeta.trigger,
        steps,
        edges: graphEdges
      };

      const response = await updateWorkflow(payload);
      if (response?.success) {
        toast.success('Workflow saved successfully.');
        setNotice('Workflow saved.');
      } else {
        toast.error(response?.message || 'Failed to save workflow.');
      }
    } catch (error) {
      console.error('Save workflow error:', error);
      toast.error('Error saving workflow.');
    } finally {
      setIsSaving(false);
    }
  }, [workflow, nodes, edges, workflowMeta, updateWorkflow]);

  useEffect(() => {
    const onSaveShortcut = (event) => {
      const isModifierPressed = event.ctrlKey || event.metaKey;
      if (isModifierPressed && event.key.toLowerCase() === 's') {
        event.preventDefault();
        saveWorkflow();
      }
    };

    window.addEventListener('keydown', onSaveShortcut);
    return () => window.removeEventListener('keydown', onSaveShortcut);
  }, [saveWorkflow]);

  const deleteSelectedEdge = useCallback(() => {
    if (!selectedEdgeId) return;
    setEdges((prev) => prev.filter((edge) => edge.id !== selectedEdgeId));
    setNotice('Selected edge deleted.');
    setSelectedEdgeId(null);
  }, [selectedEdgeId, setEdges]);

  const displayEdges = useMemo(
    () =>
      edges.map((edge) => ({
        ...edge,
        style:
          edge.id === selectedEdgeId
            ? { ...(edge.style || {}), stroke: '#dc2626', strokeWidth: 2.2 }
            : edge.style
      })),
    [edges, selectedEdgeId]
  );

  return (
    <main className={`h-full min-h-full m-0 text-slate-900 bg-slate-100 flex flex-col ${focusMode ? 'overflow-hidden p-3 gap-3' : 'overflow-y-auto p-4 gap-4'}`}>
      {/* <header className={`grid gap-4 rounded-xl border border-slate-200 bg-white shadow-sm p-4 ${focusMode ? 'hidden' : 'grid-cols-1 lg:grid-cols-[1.05fr_1.35fr]'}`}>
        <div>
          <p className="m-0 inline-block rounded-full border border-green-200 bg-green-50 px-2 py-1 text-[11px] uppercase tracking-[0.08em] text-green-800">React Flow Builder</p>
          <h1 className="m-0 mt-2 text-[clamp(1.25rem,2.2vw,1.8rem)] text-slate-900">Inline Node Workflow Studio</h1>
          <p className="m-0 text-slate-500">Every node is editable directly on canvas. No separate settings sidebar needed.</p>
        </div>

        <div className="grid gap-2 grid-cols-1 sm:grid-cols-2">
          <label className="grid gap-1 text-[13px] text-slate-700">
            Workflow Name
            <input className="w-full rounded-lg border border-slate-300 bg-white px-2.5 py-2 text-slate-900 outline-none focus:border-green-600 focus:ring-2 focus:ring-green-600/20" value={workflowMeta.name} onChange={(e) => setWorkflowMeta((p) => ({ ...p, name: e.target.value }))} />
          </label>
          <label className="grid gap-1 text-[13px] text-slate-700">
            Trigger
            <select className="w-full rounded-lg border border-slate-300 bg-white px-2.5 py-2 text-slate-900 outline-none focus:border-green-600 focus:ring-2 focus:ring-green-600/20" value={workflowMeta.trigger} onChange={(e) => setWorkflowMeta((p) => ({ ...p, trigger: e.target.value }))}>
              <option value="incoming_message">Incoming Message</option>
              <option value="scheduled_event">Scheduled Event</option>
              <option value="manual_run">Manual Run</option>
            </select>
          </label>
          <label className="grid gap-1 text-[13px] text-slate-700">
            Timezone
            <input className="w-full rounded-lg border border-slate-300 bg-white px-2.5 py-2 text-slate-900 outline-none focus:border-green-600 focus:ring-2 focus:ring-green-600/20" value={workflowMeta.timezone} onChange={(e) => setWorkflowMeta((p) => ({ ...p, timezone: e.target.value }))} />
          </label>
          <label className="grid gap-1 text-[13px] text-slate-700">
            Status
            <select className="w-full rounded-lg border border-slate-300 bg-white px-2.5 py-2 text-slate-900 outline-none focus:border-green-600 focus:ring-2 focus:ring-green-600/20" value={workflowMeta.status} onChange={(e) => setWorkflowMeta((p) => ({ ...p, status: e.target.value }))}>
              <option value="DRAFT">DRAFT</option>
              <option value="PUBLISHED">PUBLISHED</option>
            </select>
          </label>
        </div>
      </header> */}

      {/* <div className={`rounded-xl border border-slate-200 bg-white shadow-sm p-3 text-sm text-slate-600 ${focusMode ? 'hidden' : ''}`}>{notice}</div> */}

      {/* <section className={`grid flex-1 min-h-0 gap-4 ${focusMode ? 'h-full grid-cols-1' : 'h-auto grid-cols-1 xl:grid-cols-[280px_minmax(0,1fr)]'}`}> */}
      {/* <section className={`grid flex-1 min-h-0 gap-4 'h-auto grid-cols-1 xl:grid-cols-[280px_minmax(0,1fr)]`}> */}

      <section className="grid flex-1 min-h-0 gap-4 grid-cols-1 md:grid-cols-[240px_minmax(0,1fr)] xl:grid-cols-[280px_minmax(0,1fr)]">
        <aside className="rounded-xl border border-slate-200 bg-white shadow-sm p-4 h-full min-h-0 flex flex-col">
          <h2 className="m-0 text-lg font-semibold text-slate-900">Node Library</h2>
          <p className="m-0 text-slate-500">Drag and drop to canvas.</p>
          <div className="grid gap-2 overflow-y-auto min-h-0 pr-1 mt-3">
            {NODE_LIBRARY.map((node) => (
              <div key={node.kind} className="cursor-grab rounded-xl border border-slate-200 bg-slate-50 p-2 grid grid-cols-[auto_1fr] gap-2" draggable onDragStart={(e) => onDragStart(e, node.kind)}>
                <span className="mt-1 h-2.5 w-2.5 rounded-full" style={{ background: node.color }} />
                <div>
                  <strong className="text-slate-900">{node.title}</strong>
                  <p className="m-0 text-slate-500">{node.kind}</p>
                </div>
              </div>
            ))}
          </div>
        </aside>

        <div className="relative min-h-0 overflow-hidden rounded-xl border border-slate-200 bg-slate-50 shadow-sm p-0 [&_.react-flow]:h-full [&_.react-flow__controls-button]:border-slate-300 [&_.react-flow__controls-button]:bg-white [&_.react-flow__controls-button]:text-slate-900" onDragOver={onDragOver} onDrop={onDrop}>
          <div className="absolute left-3 top-3 z-10 flex flex-wrap gap-2">
            <button type="button" className="rounded-md border border-green-300 bg-green-50 px-2 py-1.5 text-xs font-semibold text-green-800" onClick={() => applyDagreLayout('LR')}>
              Horizontal Layout
            </button>
            <button type="button" className="rounded-md border border-green-300 bg-green-50 px-2 py-1.5 text-xs font-semibold text-green-800" onClick={() => applyDagreLayout('TB')}>
              Vertical Layout
            </button>
            <button type="button" className="rounded-md border border-green-300 bg-green-50 px-2 py-1.5 text-xs font-semibold text-green-800" onClick={arrangeWorkflow}>
              Organize
            </button>
            <button type="button" className="rounded-md border border-blue-200 bg-blue-50 px-2 py-1.5 text-xs font-semibold text-blue-700 disabled:opacity-65 disabled:cursor-not-allowed" onClick={saveWorkflow} disabled={isSaving}>
              {isSaving ? 'Saving...' : 'Save Workflow'}
            </button>
            {selectedEdgeId ? (
              <button type="button" className="rounded-md border border-red-200 bg-red-50 px-2 py-1.5 text-xs font-semibold text-red-700" onClick={deleteSelectedEdge}>
                Delete Connection
              </button>
            ) : null}
          </div>

          <ReactFlow
            nodes={renderNodes}
            edges={displayEdges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onEdgeClick={(_, edge) => setSelectedEdgeId(edge.id)}
            onPaneClick={() => setSelectedEdgeId(null)}
            nodeTypes={nodeTypes}
            onInit={setRf}
            fitView
            defaultEdgeOptions={{ markerEnd: { type: MarkerType.ArrowClosed } }}
          >
            <MiniMap pannable zoomable style={{ background: '#ffffff', border: '1px solid #cbd5e1' }} />
            <Controls />
            <Background gap={20} size={1.1} />
          </ReactFlow>
        </div>
      </section>

    </main>
  );
}
